import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getUserDisplayName } from '../utils/auth';

const API_BASE = 'https://vhnsv33dvk.execute-api.eu-west-1.amazonaws.com';

const ITEM_TYPES = ['watch', 'clock', 'jewellery', 'other'];

function NewRepair({ token, onLogout }) {
  const navigate = useNavigate();
  const displayName = getUserDisplayName(token);

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  const [form, setForm] = useState({
    repair_number: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address1: '',
    customer_address2: '',
    customer_town: '',
    customer_postcode: '',
    item_type: 'watch',
    item_description: '',
    fault_reported: '',
    estimate_required: false,
    final_amount: '',
    deposit_amount: '',
    notify_email: false,
    notify_sms: false,
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors([]);

    // Calculate balance_amount if deposit provided
    const payload = {
      ...form,
      repair_number: parseInt(form.repair_number),
      final_amount: form.final_amount ? parseFloat(form.final_amount) : null,
      deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
      balance_amount: (form.final_amount && form.deposit_amount)
        ? parseFloat(form.final_amount) - parseFloat(form.deposit_amount)
        : (form.final_amount ? parseFloat(form.final_amount) : null),
      created_by: 'Admin'
    };

    // Remove empty strings
    Object.keys(payload).forEach(key => {
      if (payload[key] === '') payload[key] = null;
    });

    try {
      const response = await apiFetch(
        `${API_BASE}/repairs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        },
        onLogout
      );
      if (!response) return;

      const data = await response.json();

      if (response.ok) {
        navigate(`/admin/repairs/${data.repair_number}`);
      } else if (data.errors) {
        setErrors(data.errors);
      } else {
        setErrors([data.error || 'Failed to create repair.']);
      }
    } catch (err) {
      setErrors(['Unable to connect. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="portal">
      <nav className="portal-nav">
        <button className="portal-nav-brand" onClick={() => navigate('/admin')}>Repair Shop</button>
        <div className="portal-nav-links">
          <button className="nav-btn" onClick={() => navigate('/admin')}>Dashboard</button>
          <button className="nav-btn" onClick={() => navigate('/admin/search')}>Search</button>
          <button className="nav-btn active">New Repair</button>
          <button className="nav-btn logout" onClick={handleLogoutClick}>{displayName || 'Logout'}</button>
        </div>
      </nav>

      <div className="portal-content">
        <div className="detail-header">
          <div className="detail-header-left">
            <button className="back-btn" onClick={() => navigate('/admin')}>← Back</button>
            <h1>New Repair</h1>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="form-errors">
            {errors.map((err, i) => <p key={i}>{err}</p>)}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="detail-grid">

            {/* Repair number */}
            <div className="detail-card full-width">
              <div className="detail-card-title"><h2>Repair Details</h2></div>
              <div className="intake-form-row">
                <div className="intake-field">
                  <label>Repair Number *</label>
                  <input
                    type="number"
                    value={form.repair_number}
                    onChange={(e) => handleChange('repair_number', e.target.value)}
                    placeholder="From ticket book"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Customer details */}
            <div className="detail-card">
              <div className="detail-card-title"><h2>Customer</h2></div>
              <div className="intake-form-fields">
                <div className="intake-field">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="intake-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={form.customer_phone}
                    onChange={(e) => handleChange('customer_phone', e.target.value)}
                    placeholder="07700 900000"
                  />
                </div>
                <div className="intake-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => handleChange('customer_email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="intake-field">
                  <label>Address</label>
                  <input
                    type="text"
                    value={form.customer_address1}
                    onChange={(e) => handleChange('customer_address1', e.target.value)}
                    placeholder="Address line 1"
                  />
                </div>
                <div className="intake-field">
                  <label>Address 2</label>
                  <input
                    type="text"
                    value={form.customer_address2}
                    onChange={(e) => handleChange('customer_address2', e.target.value)}
                    placeholder="Address line 2"
                  />
                </div>
                <div className="intake-field">
                  <label>Town</label>
                  <input
                    type="text"
                    value={form.customer_town}
                    onChange={(e) => handleChange('customer_town', e.target.value)}
                    placeholder="Town"
                  />
                </div>
                <div className="intake-field">
                  <label>Postcode *</label>
                  <input
                    type="text"
                    value={form.customer_postcode}
                    onChange={(e) => handleChange('customer_postcode', e.target.value)}
                    placeholder="NN13 7DZ"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Item details */}
            <div className="detail-card">
              <div className="detail-card-title"><h2>Item</h2></div>
              <div className="intake-form-fields">
                <div className="intake-field">
                  <label>Item Type *</label>
                  <select
                    value={form.item_type}
                    onChange={(e) => handleChange('item_type', e.target.value)}
                  >
                    {ITEM_TYPES.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="intake-field">
                  <label>Description</label>
                  <input
                    type="text"
                    value={form.item_description}
                    onChange={(e) => handleChange('item_description', e.target.value)}
                    placeholder="e.g. Smiths wall clock, oak case"
                  />
                </div>
                <div className="intake-field">
                  <label>Fault Reported</label>
                  <textarea
                    value={form.fault_reported}
                    onChange={(e) => handleChange('fault_reported', e.target.value)}
                    placeholder="What the customer reported..."
                    rows={3}
                  />
                </div>
                <div className="intake-field">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.estimate_required}
                      onChange={(e) => handleChange('estimate_required', e.target.checked)}
                    />
                    Estimate required before proceeding
                  </label>
                </div>
              </div>
            </div>

            {/* Payment */}
            <div className="detail-card">
              <div className="detail-card-title"><h2>Payment</h2></div>
              <div className="intake-form-fields">
                <div className="intake-field">
                  <label>Final Amount (if known)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.final_amount}
                    onChange={(e) => handleChange('final_amount', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="intake-field">
                  <label>Deposit Taken</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.deposit_amount}
                    onChange={(e) => handleChange('deposit_amount', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="detail-card feature-hidden">
              <div className="detail-card-title"><h2>Notifications</h2></div>
              <div className="intake-form-fields">
                <div className="intake-field">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.notify_email}
                      onChange={(e) => handleChange('notify_email', e.target.checked)}
                    />
                    Notify by email
                  </label>
                </div>
                <div className="intake-field feature-hidden">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.notify_sms}
                      onChange={(e) => handleChange('notify_sms', e.target.checked)}
                    />
                    Notify by SMS
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Submit */}
          <div className="form-submit">
            <button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Repair'}
            </button>
            <button type="button" className="cancel-btn" onClick={() => navigate('/admin')}>
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default NewRepair;